<template>
  <div class="terminal-launcher">
    <!-- Terminal Launch Button -->
    <v-btn
      color="primary"
      class="terminal-launch-btn"
      elevation="2"
      @click="openTerminal"
    >
      <v-icon left>mdi-console</v-icon>
      Sign on my wall
    </v-btn>

    <!-- Terminal Dialog -->
    <v-dialog
      v-model="dialogOpen"
      max-width="900px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      transition="dialog-transition"
    >
      <div class="terminal-container" :class="terminalTheme">
        <!-- Terminal Header -->
        <div class="terminal-header">
          <div class="window-controls">
            <div class="control-btn close" @click="closeTerminal"></div>
            <div class="control-btn minimize"></div>
            <div class="control-btn maximize"></div>
          </div>
          <div class="terminal-title">Git Bash - Sign the Wall</div>
        </div>

        <!-- Terminal Body -->
        <div class="terminal-body" @click="focusInput" ref="terminalBody">
          <!-- Initial Welcome -->
          <div class="terminal-line">
            <span class="terminal-info">kalculus wall sign terminal v.0.1</span>
          </div>
          <div class="terminal-line">
            <span class="terminal-command">/name &lt;your name&gt;</span> - Set your name
          </div>
          <div class="terminal-line">
            <span class="terminal-command">/message &lt;your message&gt;</span> - Set your message
          </div>
          <div class="terminal-line">
            <span class="terminal-command">/submit</span> - Submit to the wall
          </div>
          <div class="terminal-line">
            <span class="terminal-command">/clear</span> - Clear terminal
          </div>
          <div class="terminal-line">
            <span class="terminal-command">/help</span> - Show commands
          </div>
          <div class="terminal-line">
            <span class="terminal-command">/exit</span> - Close terminal
          </div>
          <div class="terminal-line"></div>

          <!-- Command History -->
          <div v-for="(entry, index) in commandHistory" :key="index" class="terminal-line">
            <template v-if="entry.type === 'command'">
              <span class="terminal-prompt-wrapper">
                <span class="terminal-user">user</span><span class="terminal-prompt">@</span><span class="terminal-path">portfolio</span>
                <span class="terminal-branch">(main)</span> <span class="terminal-prompt">$</span>
              </span>
              <span class="terminal-command">{{ entry.text }}</span>
            </template>
            <template v-else-if="entry.type === 'output'">
              <span :class="entry.class || 'terminal-info'">{{ entry.text }}</span>
            </template>
            <template v-else-if="entry.type === 'error'">
              <span class="terminal-error">{{ entry.text }}</span>
            </template>
            <template v-else-if="entry.type === 'success'">
              <span class="terminal-success">{{ entry.text }}</span>
            </template>
          </div>

          <!-- Current Input Line -->
          <div class="terminal-line current-line">
            <span class="terminal-prompt-wrapper">
              <span class="terminal-user">user</span><span class="terminal-prompt">@</span><span class="terminal-path">portfolio</span>
              <span class="terminal-branch">(main)</span> <span class="terminal-prompt">$</span>
            </span>
            <div class="terminal-input-container">
              <span class="terminal-input-text">{{ currentCommand }}</span>
              <span class="terminal-cursor" v-if="showCursor"></span>
              <input
                ref="terminalInput"
                v-model="currentCommand"
                @keydown.enter="executeCommand"
                @keydown.up="previousCommand"
                @keydown.down="nextCommand"
                class="terminal-input-field"
                spellcheck="false"
                autocomplete="off"
              />
            </div>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SubmitWallComponent',
  props: {
    apiUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dialogOpen: false,
      currentCommand: '',
      commandHistory: [],
      commandHistoryIndex: -1,
      previousCommands: [],
      userName: '',
      userMessage: '',

      showCursor: true
    };
  },
  computed: {
    terminalTheme() {
      return this.$vuetify.theme.dark ? 'terminal-dark' : 'terminal-light';
    }
  },
  mounted() {
    // Cursor blink animation
    this.cursorInterval = setInterval(() => {
      this.showCursor = !this.showCursor;
    }, 500);
  },
  beforeDestroy() {
    if (this.cursorInterval) {
      clearInterval(this.cursorInterval);
    }
  },
  methods: {
    openTerminal() {
      this.dialogOpen = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },

    closeTerminal() {
      // Confirm close if user has input
      if (this.userName || this.userMessage) {
        if (confirm('Are you sure you want to close? Your progress will be lost.')) {
          this.dialogOpen = false;
          this.resetTerminal();
        }
      } else {
        this.dialogOpen = false;
        this.resetTerminal();
      }
    },

    focusInput() {
      if (this.$refs.terminalInput) {
        this.$refs.terminalInput.focus();
      }
    },

    resetTerminal() {
      this.commandHistory = [];
      this.currentCommand = '';
      this.userName = '';
      this.userMessage = '';
      this.previousCommands = [];
      this.commandHistoryIndex = -1;
    },

    executeCommand() {
      const command = this.currentCommand.trim();
      if (!command) return;

      // Add to command history
      this.commandHistory.push({
        type: 'command',
        text: command
      });

      // Add to previous commands for navigation
      this.previousCommands.push(command);
      this.commandHistoryIndex = this.previousCommands.length;

      // Process the command
      this.processCommand(command);

      // Clear current input
      this.currentCommand = '';

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    processCommand(command) {
      const parts = command.split(' ');
      const cmd = parts[0].toLowerCase();
      const args = parts.slice(1).join(' ');

      switch (cmd) {
        case '/name':
          this.setName(args);
          break;
        case '/message':
          this.setMessage(args);
          break;
        case '/submit':
          this.submitMessage();
          break;
        case '/clear':
          this.clearTerminal();
          break;
        case '/help':
          this.showHelp();
          break;
        case '/status':
          this.showStatus();
          break;
        case '/exit':
          this.exitTerminal();
          break;
        default:
          this.commandHistory.push({
            type: 'error',
            text: `Command '${cmd}' not recognized. Type /help for available commands.`
          });
      }
    },

    setName(name) {
      if (!name) {
        this.commandHistory.push({
          type: 'error',
          text: 'Usage: /name <your name>'
        });
        return;
      }

      if (name.length < 2) {
        this.commandHistory.push({
          type: 'error',
          text: 'Name must be at least 2 characters long'
        });
        return;
      }

      if (name.length > 100) {
        this.commandHistory.push({
          type: 'error',
          text: 'Name cannot exceed 100 characters'
        });
        return;
      }

      this.userName = name;
      this.commandHistory.push({
        type: 'success',
        text: `✓ Name set to: ${name}`
      });
    },

    setMessage(message) {
      if (!message) {
        this.commandHistory.push({
          type: 'error',
          text: 'Usage: /message <your message>'
        });
        return;
      }

      if (message.length < 10) {
        this.commandHistory.push({
          type: 'error',
          text: 'Message must be at least 10 characters long'
        });
        return;
      }

      if (message.length > 500) {
        this.commandHistory.push({
          type: 'error',
          text: 'Message cannot exceed 500 characters'
        });
        return;
      }

      this.userMessage = message;
      this.commandHistory.push({
        type: 'success',
        text: `✓ Message set: "${message.length > 50 ? message.substring(0, 50) + '...' : message}"`
      });
    },

    async submitMessage() {
      if (!this.userName) {
        this.commandHistory.push({
          type: 'error',
          text: 'Name is required. Use: /name <your name>'
        });
        return;
      }

      if (!this.userMessage) {
        this.commandHistory.push({
          type: 'error',
          text: 'Message is required. Use: /message <your message>'
        });
        return;
      }

      // Show submitting status
      this.commandHistory.push({
        type: 'output',
        text: 'Committing to wall...'
      });

      try {
        const response = await axios.post(`${this.apiUrl}/messages`, {
          name: this.userName,
          message: this.userMessage
        });

        if (response.data.success) {
          this.commandHistory.push({
            type: 'success',
            text: `✓ Commit successful! Your message has been added to the wall.`
          });

          this.commandHistory.push({
            type: 'output',
            text: `[main ${Math.random().toString(36).substr(2, 7)}] Add message from ${this.userName}`
          });

          this.commandHistory.push({
            type: 'output',
            text: '1 file changed, 1 insertion(+)'
          });

          this.commandHistory.push({
            type: 'success',
            text: `✓ Successfully committed your message to the wall!`
          });

          this.commandHistory.push({
            type: 'output',
            text: 'Your message has been added to the digital wall. Thank you!'
          });

          // Clear form data so user can sign again
          this.userName = '';
          this.userMessage = '';

          // Emit success to parent component
          this.$emit('message-submitted', response.data.data);
        }
      } catch (error) {
        let errorMessage = 'Failed to submit message';

        if (error.response && error.response.data) {
          errorMessage = error.response.data.error || errorMessage;
        }

        this.commandHistory.push({
          type: 'error',
          text: `✗ Error: ${errorMessage}`
        });

        // Emit error to parent component
        this.$emit('submission-error', error);
      }

      this.scrollToBottom();
    },

    clearTerminal() {
      this.commandHistory = [];
      this.commandHistory.push({
        type: 'output',
        text: 'Terminal cleared'
      });
    },

    showHelp() {
      this.commandHistory.push({
        type: 'output',
        text: 'Available commands:'
      });
      this.commandHistory.push({
        type: 'output',
        text: '/name <name>     - Set your name'
      });
      this.commandHistory.push({
        type: 'output',
        text: '/message <text>  - Set your message'
      });
      this.commandHistory.push({
        type: 'output',
        text: '/submit         - Submit to the wall'
      });
      this.commandHistory.push({
        type: 'output',
        text: '/status         - Show current status'
      });
      this.commandHistory.push({
        type: 'output',
        text: '/clear          - Clear terminal'
      });
      this.commandHistory.push({
        type: 'output',
        text: '/help           - Show this help'
      });
      this.commandHistory.push({
        type: 'output',
        text: '/exit           - Close terminal'
      });
    },

    exitTerminal() {
      this.commandHistory.push({
        type: 'output',
        text: 'Closing terminal...'
      });

      setTimeout(() => {
        this.dialogOpen = false;
        this.resetTerminal();
      }, 500);
    },

    showStatus() {
      this.commandHistory.push({
        type: 'output',
        text: 'Current status:'
      });
      this.commandHistory.push({
        type: 'output',
        text: `Name: ${this.userName || 'not set'}`
      });
      this.commandHistory.push({
        type: 'output',
        text: `Message: ${this.userMessage ? `"${this.userMessage.length > 50 ? this.userMessage.substring(0, 50) + '...' : this.userMessage}"` : 'not set'}`
      });
    },

    previousCommand() {
      if (this.commandHistoryIndex > 0) {
        this.commandHistoryIndex--;
        this.currentCommand = this.previousCommands[this.commandHistoryIndex] || '';
      }
    },

    nextCommand() {
      if (this.commandHistoryIndex < this.previousCommands.length - 1) {
        this.commandHistoryIndex++;
        this.currentCommand = this.previousCommands[this.commandHistoryIndex] || '';
      } else if (this.commandHistoryIndex === this.previousCommands.length - 1) {
        this.commandHistoryIndex = this.previousCommands.length;
        this.currentCommand = '';
      }
    },

    scrollToBottom() {
      if (this.$refs.terminalBody) {
        this.$refs.terminalBody.scrollTop = this.$refs.terminalBody.scrollHeight;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.terminal-launcher {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.terminal-launch-btn {
  min-width: 200px;
  height: 50px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
}

.terminal-container {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.4;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);

  &.terminal-dark {
    background: #1a1a1a;
    border: 1px solid #333;
  }

  &.terminal-light {
    background: #fefefe;
    border: 1px solid #ddd;
  }
}

.terminal-header {
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  position: relative;

  .terminal-dark & {
    background: linear-gradient(135deg, #2d2d2d, #1a1a1a);
    border-bottom: 1px solid #404040;
  }

  .terminal-light & {
    background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
    border-bottom: 1px solid #ccc;
  }
}

.window-controls {
  display: flex;
  gap: 8px;
}

.control-btn {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &.close {
    background: #ff5f57;
  }

  &.minimize {
    background: #ffbd2e;
  }

  &.maximize {
    background: #28ca42;
  }
}

.terminal-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-weight: 600;
  font-size: 13px;

  .terminal-dark & {
    color: #ffffff;
  }

  .terminal-light & {
    color: #333;
  }
}

.terminal-meta {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 12px;

  .terminal-dark & {
    color: #a0a0a0;
  }

  .terminal-light & {
    color: #666;
  }
}

.terminal-body {
  padding: 20px;
  min-height: 400px;
  max-height: 500px;
  overflow-y: auto;
  position: relative;

  .terminal-dark & {
    background: #0c0c0c;
    color: #00ff00;
  }

  .terminal-light & {
    background: #ffffff;
    color: #2d5016;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    .terminal-dark & {
      background: #333;
    }

    .terminal-light & {
      background: #ccc;
    }
    border-radius: 4px;
  }
}

.terminal-line {
  margin-bottom: 4px;
  white-space: pre-wrap;
  word-break: break-word;

  &.current-line {
    display: flex;
    align-items: center;
  }

  &.success-line {
    margin-top: 16px;
    padding: 8px;
    border-radius: 4px;

    .terminal-dark & {
      background: rgba(0, 255, 0, 0.1);
      border-left: 3px solid #00ff00;
    }

    .terminal-light & {
      background: rgba(45, 80, 22, 0.1);
      border-left: 3px solid #2d5016;
    }
  }
}

.terminal-user {
  font-weight: bold;

  .terminal-dark & {
    color: #00d4ff;
  }

  .terminal-light & {
    color: #0066cc;
  }
}

.terminal-path {
  .terminal-dark & {
    color: #ff6b6b;
  }

  .terminal-light & {
    color: #cc3300;
  }
}

.terminal-branch {
  .terminal-dark & {
    color: #ffd93d;
  }

  .terminal-light & {
    color: #b8860b;
  }
}

.terminal-prompt {
  font-weight: bold;

  .terminal-dark & {
    color: #00ff00;
  }

  .terminal-light & {
    color: #2d5016;
  }
}

.terminal-command {
  .terminal-dark & {
    color: #ffffff;
  }

  .terminal-light & {
    color: #333;
  }
}

.terminal-info {
  .terminal-dark & {
    color: #a0a0a0;
  }

  .terminal-light & {
    color: #666;
  }
}

.terminal-success {
  .terminal-dark & {
    color: #00ff00;
  }

  .terminal-light & {
    color: #2d5016;
  }
}

.terminal-error {
  .terminal-dark & {
    color: #ff6b6b;
  }

  .terminal-light & {
    color: #cc3300;
  }
}

.terminal-input-container {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.terminal-input-text {
  font-family: inherit;
  font-size: inherit;
  white-space: pre;

  .terminal-dark & {
    color: #ffffff;
  }

  .terminal-light & {
    color: #333;
  }
}

.terminal-input-field {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  font-family: inherit;
  font-size: inherit;
  color: transparent;
  caret-color: transparent;

  .terminal-dark & {
    &::placeholder {
      color: rgba(255, 255, 255, 0.3);
    }
  }

  .terminal-light & {
    &::placeholder {
      color: rgba(0, 0, 0, 0.3);
    }
  }
}

.terminal-cursor {
  width: 8px;
  height: 16px;
  margin-left: 1px;
  animation: cursor-blink 1s infinite;
  flex-shrink: 0;

  .terminal-dark & {
    background: #00ff00;
  }

  .terminal-light & {
    background: #2d5016;
  }
}

@keyframes cursor-blink {
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
}

// Responsive terminal prompt wrapper
.terminal-prompt-wrapper {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 4px;
}

@media (max-width: 600px) {
  .terminal-container {
    margin: 16px;
    font-size: 12px;
  }

  .terminal-body {
    padding: 16px;
    min-height: 300px;
  }

  .terminal-launch-btn {
    min-width: 180px;
    height: 45px !important;
    font-size: 14px !important;
  }

  // Mobile-specific terminal prompt styles
  .terminal-prompt-wrapper {
    font-size: 11px;

    .terminal-user {
      display: none; // Hide "user" on mobile
    }

    .terminal-path {
      max-width: 60px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .terminal-branch {
      display: none; // Hide "(main)" on mobile
    }
  }

  .terminal-line.current-line {
    flex-wrap: nowrap;

    .terminal-prompt-wrapper {
      flex-shrink: 0;
      margin-bottom: 0;
    }

    .terminal-input-container {
      flex: 1;
      min-width: 0;
      padding: 4px 0;
    }
  }
}

@media (max-width: 400px) {
  .terminal-container {
    margin: 8px;
    font-size: 11px;
  }

  .terminal-body {
    padding: 12px;
    min-height: 280px;
  }

  // Even more compact on very small screens
  .terminal-prompt-wrapper {
    font-size: 10px;

    .terminal-path {
      max-width: 40px;
    }

    // Show minimal prompt on tiny screens
    &::before {
      content: "$ ";
      font-weight: bold;

      .terminal-dark & {
        color: #00ff00;
      }

      .terminal-light & {
        color: #2d5016;
      }
    }

    // Hide all prompt elements except the $
    > * {
      display: none;
    }
  }

  .terminal-line.current-line {
    flex-wrap: nowrap;
    align-items: center;

    .terminal-prompt-wrapper {
      flex-shrink: 0;
    }

    .terminal-input-container {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>